const MAX_ROWS = 6;
const ROW_LIFE_MS = 7000;

export interface ActivityLogEntry {
  iconSrc: string;
  text: string;
  onActivate?: (clientX: number, clientY: number) => void;
}

export interface ActivityLog {
  /** Appends one transient line (icon + text); the oldest rows drop once MAX_ROWS is exceeded and every row fades out on its own. */
  push(entry: ActivityLogEntry): void;
  /** Removes every line; used on room leave. */
  clear(): void;
}

/**
 * The bottom-left activity-log HUD: a transient, storage-free feed of room events
 * (who reset the clock / changed the tempo / changed the settings). Each row shows
 * the actor's avatar icon and a sentence, fades out after a few seconds, and — when
 * the actor is a kickable participant — is itself a tap target that opens the kick
 * UI (the second kick entry point alongside the peer-avatar tap).
 */
export function initActivityLog(): ActivityLog {
  const container = document.createElement("div");
  container.className = "activity-log";
  document.body.appendChild(container);

  function push(entry: ActivityLogEntry): void {
    const row = document.createElement("div");
    row.className = "activity-log-row";
    const img = document.createElement("img");
    img.className = "activity-log-icon";
    img.alt = "";
    img.draggable = false;
    img.src = entry.iconSrc;
    const text = document.createElement("span");
    text.className = "activity-log-text";
    text.textContent = entry.text;
    row.append(img, text);
    if (entry.onActivate !== undefined) {
      const onActivate = entry.onActivate;
      row.classList.add("activity-log-row-kickable");
      row.addEventListener("click", (ev: MouseEvent): void => onActivate(ev.clientX, ev.clientY));
    }
    container.appendChild(row);
    while (container.childElementCount > MAX_ROWS) {
      container.firstElementChild?.remove();
    }
    const anim = row.animate(
      [
        { opacity: 0, transform: "translateX(-8px)" },
        { opacity: 1, transform: "none", offset: 0.06 },
        { opacity: 1, offset: 0.8 },
        { opacity: 0 },
      ],
      { duration: ROW_LIFE_MS, easing: "ease-out", fill: "forwards" },
    );
    anim.onfinish = (): void => row.remove();
  }

  function clear(): void {
    container.replaceChildren();
  }

  return { push, clear };
}
